import React, { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../../../components/ui/Button';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Sidebar } from 'primereact/sidebar';
import { Skeleton } from 'primereact/skeleton';

import Layout from '../../Layout/Layout';
import { useGetMenu, useUpdateMenu } from '../../../queries/signageMenus';
import { useGetWallMediaLibrary } from '../../../queries/videoWall';
import useEditorDirty from './useEditorDirty';
import MenuImportDialog from './components/MenuImportDialog';
import DishImageDialog from './components/DishImageDialog';

const CURRENCIES = ['SAR', 'AED', 'KWD', 'BHD', 'QAR', 'OMR', 'JOD', 'EGP', 'IQD', 'LBP', 'USD', 'EUR'];

// Persisted content (sans row ids) — shared by save() and the dirty serializer so
// Save enables only on a real change.
const serializeMenu = (name, currency, sections) => JSON.stringify({
  name: (name || '').trim(),
  currency,
  sections: sections.map((s) => ({
    name_ar: s.name_ar || null,
    name_en: s.name_en || null,
    items: (s.items || []).map((it) => ({
      name_ar: it.name_ar || null,
      name_en: it.name_en || null,
      price: it.price || null,
      photo_media_id: it.photo_media_id || null,
      badge_ar: it.badge_ar || null,
      badge_en: it.badge_en || null,
    })),
  })),
});

let KEY_SEQ = 1;
const nextKey = () => `mk_${KEY_SEQ++}`;
const newItem = () => ({ _key: nextKey(), name_ar: '', name_en: '', price: '', photo_media_id: null, badge_ar: '', badge_en: '' });
const newSection = () => ({ _key: nextKey(), name_ar: '', name_en: '', items: [newItem()] });

/**
 * Smart Template Engine — full-page Menu editor. Bilingual sections + items
 * (AR + EN), price, optional dish photo from the wall media library. Saves the
 * whole menu (sections replaced wholesale, mirroring the playlist editor).
 * (.claude/TEMPLATE_ENGINE_SPEC.md §3)
 */
export default function VideoWallMenuEditor() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { menuId } = useParams();

  const { data: menu, isLoading } = useGetMenu(menuId);
  const update = useUpdateMenu();
  const { data: mediaRaw } = useGetWallMediaLibrary(true);

  const mediaList = useMemo(() => {
    const arr = mediaRaw?.data ?? mediaRaw ?? [];
    return Array.isArray(arr) ? arr : [];
  }, [mediaRaw]);
  const mediaMap = useMemo(() => {
    const m = {};
    mediaList.forEach((x) => { m[x.id] = x; });
    return m;
  }, [mediaList]);
  const mediaThumb = (id) => mediaMap[id]?.thumb || mediaMap[id]?.path || null;

  const [name, setName] = useState('');
  const [currency, setCurrency] = useState('SAR');
  const [sections, setSections] = useState([]);
  const [picker, setPicker] = useState(null); // { sectionKey, itemKey }
  const [importOpen, setImportOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false); // AI dish-photo dialog for `picker`
  const qc = useQueryClient();
  const { markClean, isDirty } = useEditorDirty();

  useEffect(() => {
    if (!menu) return;
    const nm = menu.name || '';
    const cur = menu.currency || 'SAR';
    const secs = (menu.sections || []).map((s) => ({
      _key: nextKey(),
      id: s.id,
      name_ar: s.name_ar || '',
      name_en: s.name_en || '',
      items: (s.items || []).map((it) => ({
        _key: nextKey(),
        id: it.id,
        name_ar: it.name_ar || '',
        name_en: it.name_en || '',
        price: it.price || '',
        photo_media_id: it.photo_media_id || null,
        badge_ar: it.badge_ar || '',
        badge_en: it.badge_en || '',
      })),
    }));
    setName(nm);
    setCurrency(cur);
    setSections(secs);
    markClean(serializeMenu(nm, cur, secs));
  }, [menu, markClean]);

  const patchSection = (key, p) => setSections((arr) => arr.map((s) => (s._key === key ? { ...s, ...p } : s)));
  const patchItem = (sk, ik, p) => setSections((arr) => arr.map((s) => (
    s._key !== sk ? s : { ...s, items: s.items.map((it) => (it._key === ik ? { ...it, ...p } : it)) }
  )));
  const addSection = () => setSections((arr) => [...arr, newSection()]);
  const removeSection = (key) => setSections((arr) => arr.filter((s) => s._key !== key));
  const moveSection = (idx, dir) => setSections((arr) => {
    const next = [...arr];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return arr;
    [next[idx], next[j]] = [next[j], next[idx]];
    return next;
  });
  const addItem = (sk) => setSections((arr) => arr.map((s) => (s._key === sk ? { ...s, items: [...s.items, newItem()] } : s)));
  const removeItem = (sk, ik) => setSections((arr) => arr.map((s) => (
    s._key !== sk ? s : { ...s, items: s.items.filter((it) => it._key !== ik) }
  )));

  const serialized = useMemo(() => serializeMenu(name, currency, sections), [name, currency, sections]);
  const dirty = isDirty(serialized);

  const save = async () => {
    if (!name.trim()) return;
    await update.mutateAsync({
      id: menuId,
      name: name.trim(),
      currency,
      sections: sections.map((s) => ({
        ...(s.id ? { id: s.id } : {}),
        name_ar: s.name_ar || null,
        name_en: s.name_en || null,
        items: s.items.map((it) => ({
          ...(it.id ? { id: it.id } : {}),
          name_ar: it.name_ar || null,
          name_en: it.name_en || null,
          price: it.price || null,
          photo_media_id: it.photo_media_id || null,
          badge_ar: it.badge_ar || null,
          badge_en: it.badge_en || null,
        })),
      })),
    });
    markClean(serialized); // saved → button disables again
  };

  const pickPhoto = (m) => {
    if (picker) patchItem(picker.sectionKey, picker.itemKey, { photo_media_id: m.id });
    setPicker(null);
  };

  // The item the photo picker / AI dialog is acting on (for AI dish-name prefill).
  const pickerItem = useMemo(() => {
    if (!picker) return null;
    const sec = sections.find((s) => s._key === picker.sectionKey);
    return sec?.items.find((it) => it._key === picker.itemKey) || null;
  }, [picker, sections]);

  // AI generated/enhanced an image → set it on the item + refresh the library.
  const onAiSaved = (media) => {
    if (picker) patchItem(picker.sectionKey, picker.itemKey, { photo_media_id: media.media_id });
    qc.invalidateQueries('videoWallMediaLibrary');
    setAiOpen(false);
    setPicker(null);
  };

  /* ── AI Menu OCR: append (or replace an empty menu with) a Gemini draft.
     Editing setSections diverges from the markClean baseline → Save enables. ── */
  const applyDraft = (draft) => {
    const incoming = (draft.sections || []).map((s) => ({
      _key: nextKey(), name_ar: s.name_ar || '', name_en: s.name_en || '',
      items: (s.items?.length ? s.items : [{}]).map((it) => ({
        _key: nextKey(), name_ar: it.name_ar || '', name_en: it.name_en || '',
        price: it.price || '', photo_media_id: null,
        badge_ar: it.badge_ar || '', badge_en: it.badge_en || '',
      })),
    }));
    if (!incoming.length) return;
    setSections((cur) => (cur.length === 0 ? incoming : [...cur, ...incoming]));
    if (draft.currency && CURRENCIES.includes(draft.currency.toUpperCase())) {
      setCurrency(draft.currency.toUpperCase());
    }
  };

  if (isLoading) return <Layout><Skeleton height="20rem" /></Layout>;
  if (!menu) return <Layout><div className="pp-card p-5 text-center">{t('video_wall.menus.not_found')}</div></Layout>;

  return (
    <Layout>
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
        <Button text icon="pi pi-arrow-left" label={t('video_wall.menus.back')}
          onClick={() => navigate('/apps/video-wall/menus')} className="p-0" />
        <div className="d-flex gap-2">
          <Button outlined icon="pi pi-sparkles" label={t('video_wall.menu_ocr.cta_button')}
            onClick={() => setImportOpen(true)} />
          <Button label={t('video_wall.library.save')} icon="pi pi-check" onClick={save}
            loading={update.isLoading} disabled={!name.trim() || !dirty} />
        </div>
      </div>

      <div className="pp-card p-4 mb-4">
        <div className="row g-3">
          <div className="col-12 col-md-8">
            <label className="d-block mb-1" style={{ color: 'var(--pp-text-secondary)', fontSize: 13 }}>
              {t('video_wall.menus.name')}
            </label>
            <InputText className="w-100" value={name} onChange={(e) => setName(e.target.value)} maxLength={150} />
          </div>
          <div className="col-12 col-md-4">
            <label className="d-block mb-1" style={{ color: 'var(--pp-text-secondary)', fontSize: 13 }}>
              {t('video_wall.menus.currency')}
            </label>
            <Dropdown className="w-100" value={currency} options={CURRENCIES.map((c) => ({ label: c, value: c }))}
              onChange={(e) => setCurrency(e.value)} />
          </div>
        </div>
      </div>

      {sections.map((sec, sIdx) => (
        <div key={sec._key} className="pp-card p-4 mb-3">
          <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
            <span className="badge" style={{ background: 'var(--pp-bg-tertiary)', color: 'var(--pp-text-secondary)' }}>
              {t('video_wall.menus.section')} {sIdx + 1}
            </span>
            <InputText style={{ maxWidth: 220 }} value={sec.name_ar} dir="rtl"
              onChange={(e) => patchSection(sec._key, { name_ar: e.target.value })}
              placeholder={t('video_wall.menus.section_name_ar')} />
            <InputText style={{ maxWidth: 220 }} value={sec.name_en}
              onChange={(e) => patchSection(sec._key, { name_en: e.target.value })}
              placeholder={t('video_wall.menus.section_name_en')} />
            <div className="ms-auto d-flex gap-1">
              <Button text rounded icon="pi pi-arrow-up" onClick={() => moveSection(sIdx, -1)} disabled={sIdx === 0} />
              <Button text rounded icon="pi pi-arrow-down" onClick={() => moveSection(sIdx, 1)} disabled={sIdx === sections.length - 1} />
              <Button text rounded icon="pi pi-trash" severity="danger" onClick={() => removeSection(sec._key)} />
            </div>
          </div>

          {sec.items.map((it) => (
            <div key={it._key} className="d-flex align-items-center gap-2 mb-2 flex-wrap"
              style={{ borderTop: '1px solid var(--pp-border-strong)', paddingTop: 8 }}>
              <Button outlined size="small" icon={it.photo_media_id ? undefined : 'pi pi-image'}
                onClick={() => setPicker({ sectionKey: sec._key, itemKey: it._key })}
                style={{ width: 48, height: 48, padding: 0, overflow: 'hidden' }}
                aria-label={t('video_wall.menus.photo')}>
                {it.photo_media_id && mediaThumb(it.photo_media_id) && (
                  <img src={mediaThumb(it.photo_media_id)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                )}
              </Button>
              <InputText style={{ maxWidth: 180 }} value={it.name_ar} dir="rtl"
                onChange={(e) => patchItem(sec._key, it._key, { name_ar: e.target.value })}
                placeholder={t('video_wall.menus.item_name_ar')} />
              <InputText style={{ maxWidth: 180 }} value={it.name_en}
                onChange={(e) => patchItem(sec._key, it._key, { name_en: e.target.value })}
                placeholder={t('video_wall.menus.item_name_en')} />
              <InputText style={{ width: 90 }} value={it.price}
                onChange={(e) => patchItem(sec._key, it._key, { price: e.target.value })}
                placeholder={t('video_wall.menus.price')} />
              <InputText style={{ width: 110 }} value={it.badge_en}
                onChange={(e) => patchItem(sec._key, it._key, { badge_en: e.target.value })}
                placeholder={t('video_wall.menus.badge')} />
              <Button text rounded icon="pi pi-times" severity="danger" className="ms-auto"
                onClick={() => removeItem(sec._key, it._key)} />
            </div>
          ))}

          <Button text size="small" icon="pi pi-plus" label={t('video_wall.menus.add_item')}
            onClick={() => addItem(sec._key)} className="mt-2" />
        </div>
      ))}

      <Button outlined icon="pi pi-plus" label={t('video_wall.menus.add_section')} onClick={addSection} />

      <MenuImportDialog visible={importOpen} onHide={() => setImportOpen(false)} onImported={applyDraft} />

      <DishImageDialog visible={aiOpen} onHide={() => setAiOpen(false)}
        initialDishName={pickerItem?.name_en || pickerItem?.name_ar || ''} onSaved={onAiSaved} />

      <Sidebar visible={!!picker} position="right" onHide={() => setPicker(null)} style={{ width: 'min(96vw, 420px)' }}>
        <h5 className="mb-3" style={{ color: 'var(--pp-text-primary)' }}>{t('video_wall.menus.pick_photo')}</h5>
        <Button className="w-100 mb-3" icon="pi pi-sparkles" label={t('video_wall.dish_ai.cta_button')}
          onClick={() => setAiOpen(true)} />
        {mediaList.length === 0 ? (
          <p className="text-muted">{t('video_wall.menus.no_media')}</p>
        ) : (
          <div className="row g-2">
            {mediaList.map((m) => (
              <div key={m.id} className="col-6">
                <div role="button" onClick={() => pickPhoto(m)} className="pp-card p-1"
                  style={{ cursor: 'pointer' }}>
                  <img src={m.thumb || m.path} alt={m.file_name}
                    className="w-100" style={{ aspectRatio: '1', objectFit: 'cover', borderRadius: 6 }} />
                </div>
              </div>
            ))}
          </div>
        )}
      </Sidebar>
    </Layout>
  );
}
